'use client';

import { useState, useRef, useEffect } from 'react';
import { Input } from '../input';

interface PhoneNumberInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  error?: boolean;
  success?: boolean;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value = '',
  onChange,
  placeholder = 'Your phone number',
  required = true,
  name = 'phone',
  id = 'phone',
  className = '',
  error = false,
  success = false,
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const cleanedValue = useRef<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Sync local state when value changes externally (e.g. form reset)
  useEffect(() => {
    const cleaned = cleanPhone(value);
    setDisplayValue(formatPhone(cleaned));
  }, [value]);

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value;
    const cleaned = cleanPhone(raw);

    // Reject if area code starts with 0 or 1
    if (cleaned.length >= 1 && /^[01]/.test(cleaned)) {
      setDisplayValue('');
      if (onChange) onChange('');
      return;
    }

    const formatted = formatPhone(cleaned);
    const prevCursor = event.currentTarget.selectionStart ?? formatted.length;

    setDisplayValue(formatted);

    // Adjust cursor position
    setTimeout(() => {
      if (!inputRef.current) return;

      let newCursor = prevCursor;

      // If user added a digit and parentheses or dash were inserted, move cursor forward
      if (cleaned.length === 1 && formatted.startsWith('(')) {
        newCursor = prevCursor + 1; // skip past '(' automatically
      } else if (cleaned.length === 4 && formatted[4] === ')') {
        newCursor = prevCursor + 2; // skip past ') '
      } else if (cleaned.length === 7 && formatted[9] === '-') {
        newCursor = prevCursor + 1; // skip past '-'
      }

      inputRef.current.setSelectionRange(newCursor, newCursor);
    }, 0);

    // Update the cleaned value but don't trigger onChange
    // Reason: onChange is triggered on blur, so we need to keep the cleaned value up to date
    if (onChange) {
      cleanedValue.current = cleaned;
      // onChange(cleaned); // Pass clean value to parent
    }
  };

  return (
    <div className="mb-4 flex items-center relative">
      <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-sm">+1</span>
      <Input
        ref={inputRef}
        required={required}
        className={`w-full ${className} pl-8`}
        type="tel"
        minLength={14} // "(123) 456-7890" length
        maxLength={14}
        placeholder={placeholder}
        value={displayValue}
        onInput={handleInput}
        onBlur={() => {
          if (onChange) onChange(cleanedValue.current);
        }}
        name={name}
        id={id}
        error={error}
        success={success}
      />
    </div>
  );
};

export default PhoneNumberInput;

// Remove all non-digits and limit to 10 digits (US numbers)
export const cleanPhone = (input: string = ''): string => {
  if (typeof input !== 'string') return '';
  return input.replace(/\D/g, '').slice(0, 10);
};

// Format clean digits into (XXX) XXX-XXXX
export const formatPhone = (digits: string = ''): string => {
  if (!digits) return '';

  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
